import {
  autoOutline,
  blank,
  discFill,
  fromCells,
  plot,
  rect,
  stamp,
  thickLine,
  toCells,
} from '../compose';
import type { PaletteInstance, SpriteDef } from '../types';
import { BOB, GALLOP_FRAME_COUNT } from './horse';

/**
 * The jockey, 56×40 to overlay the horse frame-for-frame — same canvas,
 * same anchor, so the renderer just stacks the two sprites at identical
 * coordinates. He rides the horse's own BOB table plus a small pump of
 * his own, which reads as a rider working the finish.
 *
 * Slots: 0 outline · 1 silkDark · 2 silkMain · 3 silkAccent ·
 * 4 capMain · 5 skin · 6 bootDark · 7 goggle.
 */

const W = 56;
const H = 40;

export const JOCKEY_SLOTS = [
  'outline',
  'silkDark',
  'silkMain',
  'silkAccent',
  'capMain',
  'skin',
  'bootDark',
  'goggle',
] as const;

/**
 * A racing crouch, facing right — and the silhouette is the whole job:
 * the seat rides HIGH above the horse's topline so the rider reads as a
 * separate body, not a blanket. Back slopes down to the shoulder, head
 * juts forward over the crest, one arm reaches to the neck, the near
 * knee folds up hard. autoOutline then rings the whole figure so it
 * separates from any coat colour underneath.
 */
function buildJockey(): string[] {
  const cells = toCells(blank(W, H));

  // Torso: a wedge from a high, heavy seat down to narrow shoulders.
  for (let x = 21; x <= 34; x += 1) {
    const t = (x - 21) / 13;
    const top = Math.round(7 - t * 3);
    const height = Math.round(6 - t * 2.4);
    rect(cells, x, top, 1, height, '2');
    // Underside catches less light.
    plot(cells, x, top + height - 1, '1');
  }
  // Colours: a bold sash across the back, the readable half of silks.
  for (let x = 23; x <= 33; x += 1) {
    const t = (x - 23) / 10;
    plot(cells, x, Math.round(8 - t * 3), '3');
    plot(cells, x, Math.round(9 - t * 3), '3');
  }

  // Head and cap — small. A rider's head is a sixth of him, and at this
  // scale anything larger reads as a hump on the horse's back.
  discFill(cells, 37, 4, 2, '5');
  rect(cells, 35, 2, 5, 2, '4'); // crown
  rect(cells, 39, 3, 3, 1, '4'); // peak
  plot(cells, 38, 5, '7'); // goggle glint
  plot(cells, 39, 5, '7');

  // Rein arm: shoulder down to hands at the crest.
  thickLine(cells, [33, 6], [40, 9], 3, '2');
  thickLine(cells, [40, 9], [43, 10], 2, '5');

  // Near leg: thigh forward, knee folded high, boot down the shoulder.
  thickLine(cells, [25, 11], [30, 13], 5, '1');
  thickLine(cells, [30, 13], [31, 17], 4, '6');
  rect(cells, 29, 17, 4, 2, '6');

  autoOutline(cells);
  return fromCells(cells);
}

const JOCKEY: string[] = buildJockey();

/** A rider works his hands at the gallop — a touch more travel than the
 * horse's own bob, and offset from it, so he never looks welded on. */
const PUMP = [0, 1, 1, 0, 0, -1, -1, 0, 0, 1, 1, 0];

function jockeyFrame(index: number): string[] {
  const lift = (BOB[index] ?? 0) + (PUMP[index] ?? 0);
  return stamp(blank(W, H), JOCKEY, 0, 1 + lift);
}

export const JOCKEY_RIDE: SpriteDef = {
  name: 'jockey-ride',
  w: W,
  h: H,
  paletteSlots: [...JOCKEY_SLOTS],
  frames: Array.from({ length: GALLOP_FRAME_COUNT }, (_, index) => jockeyFrame(index)),
  anchor: [28, 38],
};

function silks(
  id: string,
  main: number,
  dark: number,
  accent: number,
  cap: number,
): PaletteInstance {
  return { id: `silks-${id}`, colors: [0x1f1410, dark, main, accent, cap, 0xd9a878, 0x2a2118, 0x8fd0e0] };
}

/**
 * Racing colours. Lane 0 is the player's own silks; the rest are handed
 * out per rival so a field reads at a glance — coat tells you the horse,
 * silks tell you the stable.
 */
export const PLAYER_SILKS = silks('player', 0xd9a441, 0xa8792a, 0x2b2118, 0xd9a441);

export const RIVAL_SILKS: readonly PaletteInstance[] = [
  silks('crimson', 0xc4574a, 0x8f3a30, 0xefe8d2, 0xefe8d2),
  silks('cornflower', 0x5b78c4, 0x3b528f, 0xefe8d2, 0x5b78c4),
  silks('meadow', 0x5a9a52, 0x3d6f38, 0xd9e08a, 0xd9e08a),
  silks('plum', 0x8a5aa8, 0x5f3a76, 0xefd06a, 0x8a5aa8),
  silks('teal', 0x3f9a95, 0x2a6d69, 0xefe8d2, 0x2a2118),
  silks('rust', 0xb0693a, 0x7d4726, 0x2b2118, 0xefe8d2),
];

export function silksFor(lane: number): PaletteInstance {
  if (lane === 0) return PLAYER_SILKS;
  return RIVAL_SILKS[(lane - 1) % RIVAL_SILKS.length]!;
}
